import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, Truck, Clock, ShieldCheck } from "lucide-react";
import { Meta } from "../components/SEO";
import { cities } from "../data/cities";
import { CtaStrip } from "../components/Sections";
import { SmartImage } from "../components/SmartImage";

const regionUsps = [
  { icon: MapPin, t: "Lokaal aanwezig", d: "Bekend met de woningvoorraad in elke regio van Zuid-Holland." },
  { icon: Truck, t: "Snelle inzet", d: "Binnen 1 werkdag persoonlijk contact, opname meestal binnen een week." },
  { icon: Clock, t: "Strakke planning", d: "Vaste startdatum, dagelijkse rapportage en heldere afspraken over de oplevering." },
  { icon: ShieldCheck, t: "Veilig werken", d: "VCA-gecertificeerde medewerkers, voor bedrijfs- én particuliere projecten." },
];

export default function LocatiesPage() {
  return (
    <>
      <Meta
        title="Werkgebied — Aannemer in heel Zuid-Holland | Koning"
        description="Aannemer in Maassluis, Vlaardingen, Schiedam, Rotterdam, Westland, Den Haag en Delft. Lokale ervaring en een vast uitvoeringsteam — bekijk uw regio."
        canonicalPath="/locaties"
      />
      <section className="pt-24 pb-16 md:pt-32 md:pb-24 border-b border-koning-border" data-testid="locaties-hero">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="eyebrow mb-5">Werkgebied</div>
          <h1 className="font-display text-4xl md:text-6xl lg:text-7xl text-white tracking-tight max-w-4xl">
            Aannemer in heel <span className="italic text-koning-gold">Zuid-Holland</span>
          </h1>
          <p className="mt-6 text-koning-muted max-w-2xl leading-relaxed">
            Wij voeren renovaties en verbouwingen uit in alle grote steden van Zuid-Holland — én alles daartussenin. Selecteer hieronder uw stad voor lokale projecten en informatie.
          </p>
        </div>
      </section>

      {/* LIGHT USP BAND — premium beige met regionale beloftes */}
      <section className="py-20 md:py-24 bg-[#F5F2EA] border-b border-[#E1D9C5]" data-testid="locaties-usps">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="grid lg:grid-cols-[1fr_1.4fr] gap-12 items-end mb-14">
            <div>
              <div className="text-[10px] tracking-[0.28em] uppercase text-[#8A7430] mb-4">Onze regio-belofte</div>
              <h2 className="font-display text-3xl md:text-5xl tracking-tight text-[#1A1816]">
                Eén aannemer.<br />
                <span className="italic text-[#8A7430]">Heel Zuid-Holland</span> als thuisbasis.
              </h2>
            </div>
            <p className="text-[#4A453E] leading-relaxed text-base md:text-lg">
              Wij combineren de wendbaarheid van een lokale partij met de structuur van een groot bouwbedrijf. Of u nu in Rotterdam, Den Haag, Delft of een dorp daartussenin woont — onze service is identiek.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E1D9C5]">
            {regionUsps.map((u) => (
              <div key={u.t} className="bg-white p-8 lg:p-10 shadow-premium">
                <u.icon size={22} className="text-[#8A7430]" />
                <h3 className="font-display text-[#1A1816] text-xl mt-5">{u.t}</h3>
                <p className="text-[#6F6A60] text-sm leading-relaxed mt-3">{u.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-[#0B0B0B]" data-testid="locaties-grid">
        <div className="max-w-[1480px] mx-auto px-6 md:px-12 lg:px-20">
          <div className="mb-12">
            <div className="eyebrow mb-4">Alle steden</div>
            <h2 className="font-display text-3xl md:text-5xl text-white tracking-tight max-w-3xl">
              Selecteer uw stad voor <span className="italic text-koning-gold">lokale projecten</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cities.map((c) => (
              <Link key={c.slug} to={`/locaties/${c.slug}`} className="group img-zoom relative aspect-[4/5] block border border-koning-border hover:border-koning-gold/60 transition-colors" data-testid={`locaties-city-${c.slug}`}>
                <SmartImage src={c.image} alt={`Aannemer ${c.name}`} aspect="4/3" sizes="(max-width: 768px) 100vw, 33vw" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-koning-bg via-koning-bg/40 to-transparent" />
                <div className="absolute inset-0 p-7 flex flex-col justify-end">
                  <div className="text-koning-gold text-[10px] tracking-[0.24em] uppercase">Zuid-Holland</div>
                  <h3 className="font-display text-white text-3xl mt-2">{c.name}</h3>
                  <p className="text-koning-text-2/80 text-sm mt-2 line-clamp-2">{c.intro}</p>
                  <div className="mt-5 flex items-center gap-2 text-koning-gold text-xs tracking-[0.24em] uppercase">
                    Bekijk werkgebied <ArrowUpRight size={14} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CtaStrip
        variant="gold"
        title="Uw stad niet in de lijst?"
        subtitle="Wij komen door heel Zuid-Holland. Neem vrijblijvend contact op om de mogelijkheden te bespreken."
      />
    </>
  );
}
